import { Component } from "react";
import { canPlay } from "../patterns";

export interface FilePlayerProps {
  src: string;
  videoElement?: HTMLVideoElement;
  onMount?: (player: any) => void;
  onReady?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onLoaded?: () => void;
  onError?: (error: any) => void;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  playsInline?: boolean;
  preload?: string;
  poster?: string;
}

export default class FilePlayer extends Component<FilePlayerProps> {
  static displayName = "FilePlayer";

  static canPlay = canPlay.file;

  private mounted = false;
  private ready = false;

  componentDidMount() {
    this.mounted = true;
    this.load();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  componentDidUpdate(prevProps: FilePlayerProps) {
    if (prevProps.src !== this.props.src) {
      this.load();
    }
  }

  load = () => {
    if (!this.props.videoElement || !this.props.src) return;

    const video = this.props.videoElement;

    const handleLoadedMetadata = () => {
      if (this.mounted && !this.ready) {
        this.ready = true;
        this.props.onReady?.();
      }
    };

    const handlePlay = () => {
      if (this.mounted) {
        this.props.onPlay?.();
      }
    };

    const handlePause = () => {
      if (this.mounted) {
        this.props.onPause?.();
      }
    };

    const handleEnded = () => {
      if (this.mounted) {
        this.props.onEnded?.();
      }
    };

    const handleError = (error: any) => {
      if (this.mounted) {
        this.props.onError?.(error);
      }
    };

    const handleLoadedData = () => {
      if (this.mounted) {
        this.props.onLoaded?.();
      }
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("error", handleError);
    video.addEventListener("loadeddata", handleLoadedData);

    video.src = this.props.src;
    if (this.props.autoplay !== undefined) video.autoplay = this.props.autoplay;
    if (this.props.muted !== undefined) video.muted = this.props.muted;
    if (this.props.loop !== undefined) video.loop = this.props.loop;
    if (this.props.controls !== undefined) video.controls = this.props.controls;
    if (this.props.playsInline !== undefined)
      video.playsInline = this.props.playsInline;
    if (this.props.preload !== undefined)
      video.preload = this.props.preload as "" | "metadata" | "none" | "auto";
    if (this.props.poster !== undefined) video.poster = this.props.poster;

    this.props.onMount?.(this);

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("error", handleError);
      video.removeEventListener("loadeddata", handleLoadedData);
    };
  };

  play = () => {
    if (this.props.videoElement) {
      const video = this.props.videoElement;
      const hasValidSource =
        video.src ||
        (video.currentSrc && video.currentSrc !== "") ||
        video.readyState >= 1;

      if (hasValidSource) {
        video.play()?.catch((error) => {
          console.error("[FilePlayer] Failed to play:", error);
          this.props.onError?.(error);
        });
      } else {
        console.warn("[FilePlayer] Cannot play: video has no valid source");
      }
    }
  };

  pause = () => {
    if (this.props.videoElement) {
      this.props.videoElement.pause();
    }
  };

  stop = () => {
    this.pause();
    if (this.props.videoElement) {
      this.props.videoElement.currentTime = 0;
    }
  };

  seekTo = (seconds: number, keepPlaying?: boolean) => {
    if (this.props.videoElement) {
      this.props.videoElement.currentTime = seconds;
      if (!keepPlaying) {
        this.pause();
      }
    }
  };

  setVolume = (volume: number) => {
    if (this.props.videoElement) {
      this.props.videoElement.volume = Math.max(0, Math.min(1, volume));
    }
  };

  mute = () => {
    if (this.props.videoElement) {
      this.props.videoElement.muted = true;
    }
  };

  unmute = () => {
    if (this.props.videoElement) {
      this.props.videoElement.muted = false;
    }
  };

  setPlaybackRate = (rate: number) => {
    if (this.props.videoElement && rate > 0) {
      this.props.videoElement.playbackRate = rate;
    }
  };

  setLoop = (loop: boolean) => {
    if (this.props.videoElement) {
      this.props.videoElement.loop = loop;
    }
  };

  getDuration = (): number | null => {
    if (this.props.videoElement && isFinite(this.props.videoElement.duration)) {
      return this.props.videoElement.duration;
    }
    return null;
  };

  getCurrentTime = (): number | null => {
    if (
      this.props.videoElement &&
      isFinite(this.props.videoElement.currentTime)
    ) {
      return this.props.videoElement.currentTime;
    }
    return null;
  };

  getSecondsLoaded = (): number | null => {
    if (
      this.props.videoElement &&
      this.props.videoElement.buffered.length > 0
    ) {
      return this.props.videoElement.buffered.end(
        this.props.videoElement.buffered.length - 1
      );
    }
    return null;
  };

  getInternalPlayer = (key = "player") => {
    if (key === "video") return this.props.videoElement;
    return null;
  };

  enablePIP = async () => {
    if (
      this.props.videoElement &&
      "requestPictureInPicture" in this.props.videoElement
    ) {
      try {
        await (this.props.videoElement as any).requestPictureInPicture();
      } catch (error) {
        console.warn("Picture-in-Picture failed:", error);
      }
    }
  };

  disablePIP = async () => {
    if (document.pictureInPictureElement) {
      try {
        await document.exitPictureInPicture();
      } catch (error) {
        console.warn("Exit Picture-in-Picture failed:", error);
      }
    }
  };

  render() {
    return null;
  }
}
